import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, ModalService, PanelAPI, SegmentedControlAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID, isEmptyOrBlankString } from '@philips-internal/oswald-ui-utils';
import { EligibilityInsuranceResponse } from '../../app/types';
import { FragmentNames, PseudonymAppointmentsStep_1Aliases } from '../../codegen';
import { DEFAULT_PATIENT_ID } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import Patient from '../../interfaces/patient';
import AppointmentService from '../../services/appointment-service';
import { PatientHttpService } from '../../services/patient-http-service';
import { defaultIfUndefined } from '../../utils/object-utils';
import { showToastNotificationError, showToastNotificationSuccess, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { AppointmentEstablishment, CurrentPatientSelected, HealthInsuranceCardProps, PriorLocation, PseudonymCustomMenssage, ReleasedPlan, VisibilityFieldsResponse } from './pseudonym-interfaces';
import { AppointmentsSteps, VISIBILITY_OF_FIELD } from './pseudonym-types';
import { PredefinedValidators } from './../../utils/predefined-validators';
import { LocaleTypes } from '../../constants/locale-types';
import PrivateInsuranceConfiguration from '../../interfaces/private-insurance-configuration';
import { CUSTOM_MESSAGE_TYPE } from '../../constants/custom-message-type';

interface SimpleObjectResponse {
  value: string;
}

interface HealthInsuranceModalSubmitedProps {
  category?: HealthInsuranceCategory;
  healthCardNumber?: string;
  insurance: HealthInsurance;
  plan?: HealthInsurancePlan;
  saveInsurance: boolean;
  walletExpiryDate?: string;
}

interface HealthInsuranceCategory {
  cdCategoria: string;
  cdConvenio: number;
  dsCategoria: string;
}

interface HealthInsurancePlan {
  cdCategoria: string;
  cdConvenio: number;
  cdPlano: string;
  dsPlano: string;
}
interface HealthInsurance {
  cdConvenio: string;
  dsConvenio: string;
  ieInsurancePrivate: string;
}

interface HealthInsurancesReduced {
  insurancesCardButtons: Array<HealthInsuranceCardProps>;
  insurancesCards: Array<HealthInsuranceCardProps>;
}

interface HealthInsuranceHolderDTO {
  cdCategoria: string;
  cdConvenio: number;
  cdPlanoConvenio: string;
  cdUsuarioConvenio: string;
  dtFimVigencia: string;
  dtInicioVigencia: string;
  dtValidadeCarteira: string;
}

interface EligibilityDTO {
  beneficiary?: string;
  cardExpirationDate?: string;
  categoryCode?: string;
  insuranceCode?: number;
  naturalPersonName?: string;
  patientId?: string
  planCode?: string;
}

interface EligibilityResponseError {
  message: string;
  proceedIfHasError: boolean;
}

const HealthInsuranceCardDefaultIds = {
  NEW_ITEM_BUTTON: '#ADD_NEW_BUTTON',
  PRIVATE_BUTTON: '#PRIVATE',
};

@Controller({ component: PseudonymAppointmentsStep_1Aliases.LAYOUT_PATIENT_INFO, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_1 })
export default class PseudonymAppointmentsStep1Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_1Aliases.FORM_PATIENT_INFO)
    formPatientInfoApi!: FieldAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.FIELD_PRIOR_LOCATION)
    fieldPriorLocationApi!: FieldAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.FETCH_PRIOR_LOCATION)
    fetchPriorLocationApi!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.FETCH_PATIENT_INFO)
    fetchPatientInfoApi!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.AUTOCOMPLETE_PATIENT_INFO)
    autocompletePatientInfoApi!: AutocompleteAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.PANEL_INSURANCE)
    panelInsuranceApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.PANEL_PRIOR_LOCATION)
    panelPriorLocationApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.SEGMENTED_CONTROL_INSURANCE)
    segmentedControlInsuranceApi!: SegmentedControlAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.PANEL_LOADER_ELIGIBILITY)
    panelLoaderEligibilityApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.PANEL_ESTABLISHMENT_LIST)
    panelEstablishmentListApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_1Aliases.FIELD_ESTABLISHMENT_LIST)
    fieldEstablishmentListApi!: FieldAPI;

  private language = LanguageService.getCurrentLanguage();

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.FETCH_PATIENT_INFO })
  onMountFormPatientInfo(): void {
    const param = { nmPessoaFisica: '' };
    this.fetchPatientInfoApi.setParameters(param);
    this.fetchPatientInfoApi.forceFetch();
  }

  @OnAfterFetch({ target: PseudonymAppointmentsStep_1Aliases.FETCH_PATIENT_INFO })
  onAfterFetch(event: FetchEvent): void {
    const patientList = event.data as Array<Patient>;
    if (!patientList.length) {
      showToastNotificationError(MessageService.translate('patient.message.patient-not-found'));
      return;
    }

    const currentPatientId = Number(UserContext.patientId);
    const patientSelected = currentPatientId
      ? patientList.find(patient => patient.cdPessoaFisica === currentPatientId.toString())
      : { cdPessoaFisica: patientList[0].cdPessoaFisica, nmPessoaFisica: patientList[0].nmPessoaFisica } as Patient;

    if (!patientSelected?.cdPessoaFisica && !patientSelected?.nmPessoaFisica) {
      showToastNotificationError(MessageService.translate('patient.message.patient-not-found'));
      return;
    }

    const currentPatientSelected = this.mountCurrentPatientSelected(patientSelected);
    this.formPatientInfoApi.setData({ patient: { cdPessoaFisica: currentPatientSelected.id, nmPessoaFisica: currentPatientSelected.name } });
    this.showPatientHealthInsurance(currentPatientSelected);
  }

  @OnChange({ target: PseudonymAppointmentsStep_1Aliases.AUTOCOMPLETE_PRIOR_LOCATION })
  handleChangePriorLocationAutocomplete(event: ChangeEvent<PriorLocation>): void {
    const isPriorLocationFieldVisible = PseudonymAppointmentModel.stepOne.priorLocation.isVisible;

    if (!isPriorLocationFieldVisible) {
      return;
    }

    this.setPriorLocationValues(event.value);
    this.setWizardChoosePatientStepDone();
  }

  @OnChange({ target: PseudonymAppointmentsStep_1Aliases.AUTOCOMPLETE_ESTABLISHMENT_LIST })
  async handleChangeEstablishmentListAutocomplete(event: ChangeEvent<AppointmentEstablishment>): Promise<void> {
    this.setAppointmentEstablishment(event.value);
    await this.validateReleasedPlan();
    this.setWizardChoosePatientStepDone();
  }

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.FIELD_ESTABLISHMENT_LIST })
  onMountFieldEstablishmentList(event: MountEvent): void {
    (event.target as FieldAPI).validate();
  }

  @OnChange({ target: PseudonymAppointmentsStep_1Aliases.AUTOCOMPLETE_PATIENT_INFO })
  onChangePatientAutocomplete(event: ChangeEvent<Patient>): void {
    const patient = event.value;
    this.setWizardStepDone(AppointmentsSteps.STEP_1_CHOOSE_PATIENT, false);

    if (!patient) {
      this.panelInsuranceApi.setVisible(false);
      return;
    }

    const currentPatientSelected = this.mountCurrentPatientSelected(patient);
    const isPatientSelected = (currentPatientSelected.id && currentPatientSelected.name);

    if (isPatientSelected) {
      this.showPatientHealthInsurance(currentPatientSelected);
    }

    PseudonymAppointmentModel.stepTwo.clearFields();
  }

  private mountCurrentPatientSelected(patient: Patient): CurrentPatientSelected {
    const currentPatientSelected = {
      id: Number(defaultIfUndefined(patient.cdPessoaFisica, DEFAULT_PATIENT_ID)),
      name: defaultIfUndefined(patient.nmPessoaFisica, '')
    } as CurrentPatientSelected;

    return currentPatientSelected;
  }

  private showPatientHealthInsurance(currentPatientSelected: CurrentPatientSelected): void {
    this.panelInsuranceApi.setVisible(true);
    this.loadPatientHealthInsurancesAndShow(currentPatientSelected.id);
    PseudonymAppointmentModel.stepOne.currentPatientSelected = currentPatientSelected;
  }

  private loadPatientHealthInsurancesAndShow(selectedPatientId: number): void {
    const privateHealthInsurance = HttpService.get(APPLICATION_ENDPOINTS.PARTICULAR_INSURANCE, {})
      .then(response => response.body as PrivateInsuranceConfiguration);

    const healthInsuranceList = HttpService.get(APPLICATION_ENDPOINTS.GET_MY_INSURANCES_DATA_CARD, { patientId: selectedPatientId })
      .then((response => response.body as Array<HealthInsuranceCardProps>));

    Promise.all([privateHealthInsurance, healthInsuranceList])
      .then(([privateHealthInsuranceIdResult, healthInsuranceListResult]) => {
        const lastDefaultItems = [
          { cardId: HealthInsuranceCardDefaultIds.PRIVATE_BUTTON, categoryId: privateHealthInsuranceIdResult.categoryCode, healthInsuranceId: privateHealthInsuranceIdResult.insuranceCode, isPrivate: true },
          { cardId: HealthInsuranceCardDefaultIds.NEW_ITEM_BUTTON, isNewItemButton: true }
        ] as unknown as Array<HealthInsuranceCardProps>;

        healthInsuranceListResult.push(...lastDefaultItems);
        if (typeof this.segmentedControlInsuranceApi.setData === 'function') {
          this.segmentedControlInsuranceApi.setData(healthInsuranceListResult as unknown as Array<Record<string, Value>>);
        } else {
          this.historyService.push(APPLICATION_ROUTES.APPOINTMENTS, {}, {scrollToTop: true});
        }
      })
      .catch(() => this.showToastNotificationLoadDataError());
  }

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.PANEL_INSURANCE })
  onMountPanelInsurance(): void {
    this.panelInsuranceApi.setVisible(false);
  }

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.PANEL_PRIOR_LOCATION })
  handleMountPanelPriorLocation(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_VISIBILITY_OF_FIELDS)
      .then(response => {
        const body = response.body as VisibilityFieldsResponse;
        this.showPriorLocationOptions(this.fieldPriorLocationApi, body.priorLocation);
      })
  }

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.PANEL_ESTABLISHMENT_LIST })
  handleMountPanelEstablishmentList(): void {
    this.getEstablishmentListVisibility().then(visibility => {
      this.panelEstablishmentListApi.setVisible(visibility);
      if (visibility) {
        this.setWizardHiddenStep(AppointmentsSteps.STEP_5_LOCATION);
      }
    });
  }

  @OnMount({ target: PseudonymAppointmentsStep_1Aliases.PANEL_LOADER_ELIGIBILITY })
  onMountPanelLoaderEligibility(): void {
    this.panelLoaderEligibilityApi.setVisible(false);
  }

  @OnBeforeFetch({ target: PseudonymAppointmentsStep_1Aliases.FETCH_PRIOR_LOCATION })
  handleBeforeFetchPriorLocation(event: FetchEvent): void {
    if (PseudonymAppointmentModel.stepOne.priorLocation.isVisible) {
      return;
    }

    const fetchPriorLocation = event.target as FetchAPI;
    const valueFieldPriorLocation = this.fieldPriorLocationApi.getValue() as string;

    if (!isEmptyOrBlankString(valueFieldPriorLocation)) {
      fetchPriorLocation.setParameters({ 'priorLocation': valueFieldPriorLocation });
    }
  }

  @OnChange({ target: PseudonymAppointmentsStep_1Aliases.SEGMENTED_CONTROL_INSURANCE })
  async onChangeSegmentedControl(event: ChangeEvent<Array<HealthInsuranceCardProps>>): Promise<void> {
    const selectedValue = event.value?.length === 1 ? event.value[0] : undefined as unknown as HealthInsuranceCardProps;

    if (selectedValue) {
      PseudonymAppointmentModel.stepOne.healthInsurance = selectedValue;

      if (selectedValue.isNewItemButton) {
        const tasyInsuranceFieldParameter = await this.getTasyInsuranceFieldParameter();

        ModalService.openDefault({
          closeable: true,
          contextData: { tasyInsuranceParameter: tasyInsuranceFieldParameter },
          dockButtons: [],
          fragment: FragmentNames.INSURANCE_AUTOCOMPLETE_MODAL,
          onClose: () => this.segmentedControlInsuranceApi.setValue([]),
          onSubmit: (eventSubmit: SubmitEvent) => this.handleSubmitInsuranceAutocompleteModal(eventSubmit),
          title: MessageService.translate('tws.label.add-insurance'),
        });
      }

      this.handleHealthInsuranceEligibility(selectedValue)
        .finally(() => {
          this.setWizardChoosePatientStepDone();
        });

      if (!selectedValue.isNewItemButton) {
        AppointmentService.messageOnInsuranceSelection(selectedValue);
      }

      this.showLocationStep(selectedValue.healthInsuranceId);

      await this.validateReleasedPlan();

      PseudonymAppointmentModel.stepTwo.clearFields();
    } else {
      this.setWizardStepDone(AppointmentsSteps.STEP_1_CHOOSE_PATIENT, false);
    }
  }

  private getTasyInsuranceFieldParameter(): Promise<string> {
    return HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.TASY_INSURANCE_FIELD_PARAMETER)
      .then(response => response.body?.value)
      .then(response => response ? response : '');
  }

  private handleSubmitInsuranceAutocompleteModal(event: SubmitEvent): void {
    const record = event.record as HealthInsuranceModalSubmitedProps;
    if (Object.entries(record).length === 0) {
      return;
    }

    const currentInsurances = this.segmentedControlInsuranceApi.getData() as unknown as Array<HealthInsuranceCardProps>;
    const healthInsurancesReduced = currentInsurances.reduce<HealthInsurancesReduced>((insurances, item) => {
      if (item.cardId !== HealthInsuranceCardDefaultIds.PRIVATE_BUTTON && item.cardId !== HealthInsuranceCardDefaultIds.NEW_ITEM_BUTTON) {
        insurances.insurancesCards.push(item)
      } else {
        insurances.insurancesCardButtons.push(item);
      }
      return insurances;
    }, { insurancesCardButtons: [], insurancesCards: [] });

    const newInsuranceItem = {
      cardId: generateUUID(),
      categoryDesc: record.category?.dsCategoria,
      categoryId: record.category?.cdCategoria,
      healthCardNumber: record.healthCardNumber,
      healthInsuranceDesc: record.insurance.dsConvenio,
      healthInsuranceId: record.insurance.cdConvenio,
      planDesc: record.plan?.dsPlano,
      planId: record.plan?.cdPlano,
      walletExpiryDate: record.walletExpiryDate,
    } as unknown as HealthInsuranceCardProps;

    const newInsurances = healthInsurancesReduced.insurancesCards;
    newInsurances.push(newInsuranceItem);
    newInsurances.push(...healthInsurancesReduced.insurancesCardButtons);

    this.segmentedControlInsuranceApi.setData(newInsurances as unknown as Array<Record<string, Value>>);
    this.segmentedControlInsuranceApi.setValue([{ ...newInsuranceItem }]);

    if (record.saveInsurance) {
      const insurance = {
        cdCategoria: newInsuranceItem.categoryId,
        cdConvenio: newInsuranceItem.healthInsuranceId,
        cdPlanoConvenio: newInsuranceItem.planId,
        cdUsuarioConvenio: newInsuranceItem.healthCardNumber,
        dtValidadeCarteira: newInsuranceItem.walletExpiryDate,
      } as HealthInsuranceHolderDTO;

      this.saveInsurance(insurance);
    }
  }

  private updateCategoryVat(cardId: string, vatInfo: EligibilityInsuranceResponse): void {
    if (!vatInfo.sequence) {
      PseudonymAppointmentModel.stepOne.healthInsurance.sequenceVat = 0;
      return;
    }
    PseudonymAppointmentModel.stepOne.healthInsurance.sequenceVat = vatInfo.sequence;
    const cardList = this.segmentedControlInsuranceApi.getData().map(card => {
      if (card.cardId === cardId) {
        card.ivaCategory = vatInfo.categoryDescription;
        card.sequenceVat = vatInfo.sequence;
      }
      return card;
    }) as unknown as Record<string, Value>[];

    this.segmentedControlInsuranceApi.setData(cardList);
  }

  private saveInsurance(insurance: HealthInsuranceHolderDTO): void {
    const body = [insurance] as unknown as Record<string, Value>;
    const currentPatientSelected = PseudonymAppointmentModel.stepOne.currentPatientSelected;

    HttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_CREATE_INSURANCE, { patientId: currentPatientSelected.id }, { body })
      .then(() => showToastNotificationSuccess(MessageService.translate('insurance.new-insurance-added')))
      .catch((error) => showToastNotificationError(error?.message));
  }

  private handleHealthInsuranceEligibility(selectedValue: HealthInsuranceCardProps): Promise<void> {
    this.setWizardStepDone(AppointmentsSteps.STEP_1_CHOOSE_PATIENT, false);

    if (selectedValue.cardId !== HealthInsuranceCardDefaultIds.PRIVATE_BUTTON && selectedValue.cardId !== HealthInsuranceCardDefaultIds.NEW_ITEM_BUTTON) {
      this.autocompletePatientInfoApi.setEnabled(false);
      this.panelLoaderEligibilityApi.setVisible(true);

      return this.checkHealthInsuranceEligibility(selectedValue)
        .then(response => {
          const eligibility = response.body as EligibilityInsuranceResponse;
          if (this.language === LocaleTypes.ARGENTINA) {
            this.updateCategoryVat(selectedValue.cardId, eligibility);
          } else if (!eligibility.eligibility.eligible) {
            this.segmentedControlInsuranceApi.setValue([]);
            showToastNotificationWarning(MessageService.translate('tws.text.eligibility-insurance.denial'));
          }
        })
        .catch(error => {
          const responseError = error?.body as EligibilityResponseError;

          if (responseError && !responseError.proceedIfHasError) {
            this.segmentedControlInsuranceApi.setValue([]);
            showToastNotificationWarning(responseError.message);
          }
        })
        .finally(() => {
          this.autocompletePatientInfoApi.setEnabled(true);
          this.panelLoaderEligibilityApi.setVisible(false);
        });
    }

    return Promise.resolve();
  }

  private checkHealthInsuranceEligibility(healthInsuranceCard: HealthInsuranceCardProps): Promise<HttpResponse<EligibilityInsuranceResponse>> {
    const currentPatientSelected = PseudonymAppointmentModel.stepOne.currentPatientSelected;
    const autocompletePatientInfoData = this.autocompletePatientInfoApi.getValue() as unknown as Patient;
    const params = {
      beneficiary: healthInsuranceCard.healthCardNumber,
      cardExpirationDate: healthInsuranceCard.walletExpiryDate,
      categoryCode: healthInsuranceCard.categoryId,
      insuranceCode: healthInsuranceCard.healthInsuranceId,
      naturalPersonName: currentPatientSelected.name,
      patientId: autocompletePatientInfoData.cdPessoaFisica,
      planCode: healthInsuranceCard.planId,
    } as EligibilityDTO;

    return PatientHttpService.post(APPLICATION_ENDPOINTS.CHECK_INSURANCE_ELIGIBILITY, {}, { body: { ...params } });
  }

  private setWizardChoosePatientStepDone(): void {
    const isInsuranceSelected = this.segmentedControlInsuranceApi.getValue().length > 0;
    const isPriorLocationFieldVisible = PseudonymAppointmentModel.stepOne.priorLocation.isVisible;
    const isPriorLocationIsValid = PseudonymAppointmentModel.stepOne.priorLocation.sequenceNumber !== 0;
    const isAppointmentEstablishmentValid = PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment > 0;
    const isAppointmentEstablishmentVisible = this.panelEstablishmentListApi.isVisible();
    const isReleasedPlan = PseudonymAppointmentModel.stepOne.releasedPlan;

    const conditionPriorLocation = !isPriorLocationFieldVisible || isPriorLocationIsValid || this.fieldPriorLocationApi.getValidators().length === 0;
    const conditionAppointmentEstablishment = !isAppointmentEstablishmentVisible || isAppointmentEstablishmentValid;
    const finalCondition =
      isInsuranceSelected
      && conditionPriorLocation
      && conditionAppointmentEstablishment
      && isReleasedPlan;
    this.setWizardStepDone(AppointmentsSteps.STEP_1_CHOOSE_PATIENT, finalCondition);
  }

  private showPriorLocationOptions(fieldPriorLocationApi: FieldAPI, isFieldPriorLocationVisible: string): void {
    switch (isFieldPriorLocationVisible) {
      case VISIBILITY_OF_FIELD.NOT_SHOW:
        this.setConfigurationFieldPriorLocationVisible(fieldPriorLocationApi, false, false);
        break;
      case VISIBILITY_OF_FIELD.REQUIRED:
        this.setConfigurationFieldPriorLocationVisible(fieldPriorLocationApi, true, true);
        break;
      case VISIBILITY_OF_FIELD.SHOW:
        this.setConfigurationFieldPriorLocationVisible(fieldPriorLocationApi, true, false);
        break;
      default:
        this.setConfigurationFieldPriorLocationVisible(fieldPriorLocationApi, false, false);
        this.panelPriorLocationApi.setVisible(false);
        break;
    }
  }

  private setConfigurationFieldPriorLocationVisible(fieldPriorLocationApi: FieldAPI, visible: boolean, required: boolean): void {
    const requiredValues = PredefinedValidators.required();
    fieldPriorLocationApi.setVisible(visible);
    fieldPriorLocationApi.setValidators(required ? [requiredValues] : []);

    PseudonymAppointmentModel.stepOne.priorLocation.isVisible = visible;
  }

  private setPriorLocationValues(newPriorValue: PriorLocation): void {
    const isPriorLocationFieldVisible = PseudonymAppointmentModel.stepOne.priorLocation.isVisible;

    const priorLocationValue = {
      description: newPriorValue?.description ? newPriorValue.description : '',
      isVisible: !!isPriorLocationFieldVisible,
      sequenceNumber: newPriorValue?.sequenceNumber ? newPriorValue.sequenceNumber : 0
    } as PriorLocation

    PseudonymAppointmentModel.stepOne.priorLocation = priorLocationValue;
  }

  private setAppointmentEstablishment(newPriorValue: AppointmentEstablishment): void {

    PseudonymAppointmentModel.stepOne.appointmentEstablishment = {
      cdEstablishment: newPriorValue?.cdEstablishment ? newPriorValue.cdEstablishment : 0,
      dsEstablishmentName: newPriorValue?.dsEstablishmentName ? newPriorValue.dsEstablishmentName : ''
    } as AppointmentEstablishment;

  }

  private async validateReleasedPlan(): Promise<void> {
    if (this.enterValidateReleasedPlan()) {
      PseudonymAppointmentModel.stepOne.releasedPlan = true;
      return;
    }
    const requestBody = {
      category: PseudonymAppointmentModel.stepOne.healthInsurance?.categoryId || '',
      establishment: PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment || 0,
      establishmentFilter: PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment ? 'S' : 'N',
      insurance: PseudonymAppointmentModel.stepOne.healthInsurance?.healthInsuranceId || 0,
      plan: PseudonymAppointmentModel.stepOne.healthInsurance?.planId ?? ''
    };

    await HttpService.post(APPLICATION_ENDPOINTS.GET_RELEASED_PLANS_BY_ESTABLISHMENT, {}, { body: requestBody })
      .then(response => {
        const body = response.body as ReleasedPlan;
        if (body.released === 'N') {
          this.showMessageInsuranceRelationship();
          PseudonymAppointmentModel.stepOne.releasedPlan = false;
          this.segmentedControlInsuranceApi.setValue([]);
        } else {
          PseudonymAppointmentModel.stepOne.releasedPlan = true;
        }
      });
  }

  private enterValidateReleasedPlan(): boolean {
    return !this.panelEstablishmentListApi.isVisible() || (
      (!PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment || PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment === 0) ||
      (PseudonymAppointmentModel.stepOne.healthInsurance.healthInsuranceId === 0 || PseudonymAppointmentModel.stepOne.healthInsurance.healthInsuranceId === undefined) ||
      PseudonymAppointmentModel.stepOne.healthInsurance?.cardId === HealthInsuranceCardDefaultIds.PRIVATE_BUTTON
    );
  }

  private showMessageInsuranceRelationship(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { action: CUSTOM_MESSAGE_TYPE.INSURANCE_RELATIONSHIP }).then(customMessageResponse => {
      const customMessage = customMessageResponse.body as PseudonymCustomMenssage;
      const expression = customMessage?.message || MessageService.translate('tws.text.eligibility-insurance.denial');
      showToastNotificationWarning(expression);
    })
  }
}
